/**
 * Genesis's look, shared by the admin pages and the page editor: quiet
 * aluminum chrome, graphite type, clay for AI work not yet kept, and
 * dashboard-lamp colors for state. Barlow (drawn from California road signs)
 * for the interface, IBM Plex Mono for paths and versions.
 *
 * Colors are CSS variables with a light and a dark value (shared/lib/adminTheme),
 * so the same styles work in both themes. Use these names, not hex values.
 */
import { themeVar as v } from 'shared/lib/adminTheme';

export const S = {
  bg: v('bg'),
  chrome: v('chrome'),
  chromeHover: v('chromeHover'),
  line: v('line'),
  field: v('field'),
  fieldLine: v('fieldLine'),
  fg: v('fg'),
  /** Hover and pressed shades of fg, for fg-filled (primary) buttons. */
  fgHover: v('fgHover'),
  fgActive: v('fgActive'),
  /** Text and icons on an fg-filled background: white in light, near-black in dark. */
  onFg: v('onFg'),
  muted: v('muted'),
  wall: v('wall'),
  wallGrid: v('wallGrid'),
  clay: v('clay'),
  clayInk: v('clayInk'),
  claySoft: v('claySoft'),
  green: v('green'),
  amber: v('amber'),
  red: v('red'),
  redHover: v('redHover'),
  blue: v('blue'),
  /** Dims the screen behind dialogs. */
  scrim: v('scrim'),
  ui: '"Barlow", "Helvetica Neue", Arial, sans-serif',
  label: '"Barlow Semi Condensed", "Barlow", "Arial Narrow", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace'
};

/**
 * Key for dark-only styles inside a react-jss rule, for colors that have no
 * token (the light value stays exactly as written):
 *   item: { background: 'hsl(210, 20%, 96%)', [DARK]: { background: S.chrome } }
 * Don't use it in anything rendered on the page canvas or in site previews:
 * those stay light in a dark admin.
 */
export const DARK = '[data-gx-theme="dark"] &';

/** Small uppercase label style used for panel titles and table headings. */
export const labelStyle = {
  fontFamily: S.label,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: S.muted
};

/** Extra values for the admin pages: white work surfaces on the aluminum chrome. */
export const A = {
  surface: v('surface'),
  rowLine: v('rowLine'),
  rowHover: v('rowHover'),
  rowSelected: v('rowSelected'),
  radius: 8,
  pop: v('pop'),
  focus: { outline: `2px solid ${S.fg}`, outlineOffset: 2 }
};

/** Status lamps: text, tint and dot for each tone. */
export const TONES = {
  green: { fg: v('toneGreenFg'), bg: v('toneGreenBg'), dot: S.green },
  amber: { fg: v('toneAmberFg'), bg: v('toneAmberBg'), dot: S.amber },
  red: { fg: v('toneRedFg'), bg: v('toneRedBg'), dot: S.red },
  blue: { fg: v('toneBlueFg'), bg: v('toneBlueBg'), dot: S.blue },
  gray: { fg: v('toneGrayFg'), bg: v('toneGrayBg'), dot: v('toneGrayDot') }
};

export type Tone = keyof typeof TONES;
